import { ChevronDown } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown';
import { cn } from '../helpers/cn';

export type PillSelectItem<T extends string> = {
  value: T;
  label: string;
};

export type PillSelectProps<T extends string> = {
  // The options to render, in display order.
  options: PillSelectItem<T>[];
  // The value of the currently selected option.
  value: T;
  // Called with the option value when a new option is selected.
  onChange: (value: T) => void;
  // Accessible label for the trigger (e.g. "Select subject").
  ariaLabel?: string;
  // Extra classes for the trigger button.
  className?: string;
};

// Rounded pill single-select, styled to match TabNav. Generic over the option
// value type so each page can pass its own union and keep `value`/`onChange`
// fully typed.
function PillSelect<T extends string>({ options, value, onChange, ariaLabel, className }: PillSelectProps<T>) {
  const selected = options.find((option) => option.value === value);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={ariaLabel}
          className={cn('w-full justify-between gap-3 border-grey-200 p-3 font-[Poppins] font-normal', className)}
        >
          <span className="truncate">{selected?.label}</span>
          <ChevronDown className="h-4 w-4 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[var(--radix-dropdown-menu-trigger-width)]">
        {options.map((option) => (
          <DropdownMenuItem key={option.value} onSelect={() => onChange(option.value)}>
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default PillSelect;
